import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink } from "lucide-react";

interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  created_at: string;
}

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
}

interface Order {
  user_id: string;
  email: string | null;
}

const statusColors: Record<string, string> = {
  active: "bg-green-600 text-white",
  trialing: "bg-blue-600 text-white",
  canceled: "bg-destructive text-destructive-foreground",
  inactive: "bg-muted text-muted-foreground",
};

export default function AdminSubscriptions() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: subData }, { data: profileData }, { data: orderData }] = await Promise.all([
        supabase.from("subscriptions").select("*").order("created_at", { ascending: false }),
        supabase.from("profiles").select("*"),
        supabase.from("orders").select("user_id, email"),
      ]);
      setSubs((subData as Subscription[]) ?? []);
      const pMap: Record<string, Profile> = {};
      (profileData ?? []).forEach((p: Profile) => { pMap[p.id] = p; });
      setProfiles(pMap);
      const eMap: Record<string, string> = {};
      (orderData ?? []).forEach((o: Order) => { if (o.email) eMap[o.user_id] = o.email; });
      setEmails(eMap);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Subscriptions</h1>
      {subs.length === 0 ? (
        <p className="text-muted-foreground">Keine Subscriptions vorhanden.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kunde</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Laufzeit-Ende</TableHead>
                <TableHead>Stripe</TableHead>
                <TableHead>Erstellt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map((sub) => {
                const profile = profiles[sub.user_id];
                const name = profile ? [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.company || sub.user_id.slice(0, 8) : sub.user_id.slice(0, 8);
                const email = emails[sub.user_id];
                return (
                  <TableRow key={sub.id}>
                    <TableCell>{name}</TableCell>
                    <TableCell className="text-xs">{email || "—"}</TableCell>
                    <TableCell><Badge variant="secondary">{sub.plan_id}</Badge></TableCell>
                    <TableCell>
                      <Badge className={statusColors[sub.status] ?? ""}>{sub.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString("de-DE") : "—"}
                    </TableCell>
                    <TableCell>
                      {sub.stripe_subscription_id ? (
                        <a
                          href={`https://dashboard.stripe.com/subscriptions/${sub.stripe_subscription_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {sub.stripe_subscription_id.slice(0, 16)}…
                        </a>
                      ) : "—"}
                    </TableCell>
                    <TableCell>{new Date(sub.created_at).toLocaleDateString("de-DE")}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
